import type { ArticleBlock, ArticleSection } from '../../types/article'

export const expiredStockIntro: ArticleBlock[] = [
  {
    type: 'p',
    text: 'Every month, small shops across Rwanda throw away milk, yoghurt, bread, juice, and medicines that expired on the shelf. Each item might only cost a few hundred francs, but together they can quietly eat a big part of your profit.',
  },
  {
    type: 'p',
    text: 'The good news: most expired stock is avoidable. With a few simple habits — and the right records — you can sell almost everything before its date runs out.',
  },
]

export const expiredStockSections: ArticleSection[] = [
  {
    id: 'real-cost',
    title: 'What expired stock really costs you',
    blocks: [
      {
        type: 'p',
        text: 'When a product expires, you don’t just lose what you paid for it. You also lose the profit you would have made, the shelf space it took, and sometimes the trust of a customer who picked it up.',
      },
      {
        type: 'table',
        caption: 'Example: one month of waste in a Kicukiro mini supermarket',
        head: ['Product', 'Units expired', 'Cost per unit', 'Money lost'],
        rows: [
          ['Fresh milk 500ml', '24', 'RWF 400', 'RWF 9,600'],
          ['Yoghurt 500ml', '15', 'RWF 900', 'RWF 13,500'],
          ['Sliced bread', '18', 'RWF 1,200', 'RWF 21,600'],
          ['Mango juice 1L', '10', 'RWF 1,500', 'RWF 15,000'],
          ['Total', '67', '—', 'RWF 59,700'],
        ],
      },
      {
        type: 'p',
        text: 'RWF 59,700 a month is more than RWF 700,000 a year — enough to pay a shop assistant for several months.',
      },
      {
        type: 'takeaway',
        text: 'Expired stock is lost cash, not just lost goods. Measuring it every month is the first step to reducing it.',
      },
    ],
  },
  {
    id: 'why-it-happens',
    title: 'Why products expire on your shelves',
    blocks: [
      { type: 'p', text: 'In most shops, stock expires for the same few reasons:' },
      {
        type: 'ul',
        items: [
          'You order the same quantity every week, even when sales slow down.',
          'New deliveries go in front of older stock, so the old items stay at the back.',
          'Nobody checks dates until a customer complains.',
          'A supplier offers a discount for buying in bulk, and you buy more than you can sell.',
        ],
      },
      {
        type: 'image',
        src: 'https://cdn.magicpatterns.com/patterns/generated-images/5c68bbb7-8987-4194-8f0e-9d3bfa95eafe.jpg',
        alt: 'Shop assistant moving older cartons of milk and juice to the front of a storeroom shelf',
        caption: 'Moving older stock to the front is the simplest habit that prevents waste.',
      },
    ],
  },
  {
    id: 'five-habits',
    title: 'Five habits that stop the waste',
    blocks: [
      {
        type: 'h3',
        text: '1. First expiring, first out',
      },
      {
        type: 'p',
        text: 'When a delivery arrives, put the new stock behind what you already have. The items that expire first should always be the easiest for customers to reach.',
      },
      {
        type: 'h3',
        text: '2. Check dates on a fixed day',
      },
      {
        type: 'p',
        text: 'Choose one quiet morning each week — for many shops, Monday — and check the dates on everything fresh or perishable.',
      },
      {
        type: 'tip',
        text: 'Keep a small “sell first” basket near the till for items that expire within 7 days. Customers often pick them up at a small discount.',
      },
      {
        type: 'cta',
        title: 'Let Kayko watch the dates for you',
        text: 'Record batch and expiry dates when stock arrives, and get alerts before anything expires.',
        label: 'See how Inventory works',
        href: '/products/inventory',
      },
      {
        type: 'h3',
        text: '3. Order based on what you sell',
      },
      {
        type: 'p',
        text: 'Look at how many units you actually sold last week before placing the next order. If you sold 30 cartons of milk, don’t order 50.',
      },
      {
        type: 'h3',
        text: '4. Discount early, not late',
      },
      {
        type: 'p',
        text: 'A 20% discount a week before expiry recovers most of your cost. Waiting until the last day usually means throwing it away.',
      },
      {
        type: 'h3',
        text: '5. Talk to your suppliers',
      },
      {
        type: 'p',
        text: 'Many distributors in Kigali will swap slow-moving or near-expiry items if you ask early. Keep a record of what you return and when.',
      },
      {
        type: 'quote',
        text: 'Once I started checking dates every Monday, I stopped throwing away bread and milk every week.',
        cite: 'Jemima Nyirasafari, All-in-one supermarket',
      },
    ],
  },
  {
    id: 'weekly-routine',
    title: 'A simple 15-minute weekly routine',
    blocks: [
      { type: 'p', text: 'Put these steps on the wall of your storeroom and follow them every week:' },
      {
        type: 'ol',
        items: [
          'Walk the shelves and fridge, and note anything expiring within 14 days.',
          'Move those items to the front, or into your “sell first” basket.',
          'Mark items expiring within 7 days for a small discount.',
          'Compare last week’s sales with your next order, and reduce slow items.',
          'Write down anything that expired, with its cost, so you can track waste each month.',
        ],
      },
      {
        type: 'takeaway',
        text: 'Fifteen minutes a week, done every week, will save more money than any one big clean-out.',
      },
    ],
  },
]
